import React, { useMemo } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { buildSheetWorkshopQrValue } from '../../../services/lotScanResolve';
import { computeAlignersTotalFromRanges } from './ProductionSheetBody';

/**
 * En-tête imprimable : QR en haut à droite + infos patient / cas / lot.
 * Le QR encode une URL atelier (?batch=) scannable depuis /atelier-lot.
 */
const ProductionSheetHeader = ({ patientData, batch, extraFields = [] }) => {
  const batchNumber = batch?.batch_number || batch?.batchNumber || '—';
  const alignersTotal = computeAlignersTotalFromRanges(batch);
  const deadline = batch?.deadline
    ? new Date(batch.deadline).toLocaleDateString('fr-FR')
    : '—';

  const qrValue = useMemo(
    () =>
      buildSheetWorkshopQrValue({
        batchId: batch?.id || null,
        caseId: patientData?.caseId || batch?.case_id || null,
        batchNumber: batchNumber !== '—' ? batchNumber : null,
        caseNumber: patientData?.caseNumber || null,
      }),
    [batch, patientData, batchNumber]
  );

  const fields = [
    { label: 'Patient', value: patientData?.patientName },
    { label: 'N° Patient', value: patientData?.patientId },
    { label: 'Date de naissance', value: patientData?.dateOfBirth },
    { label: 'N° Cas', value: patientData?.caseNumber },
    { label: 'Praticien', value: patientData?.dentistName },
    { label: 'Clinique', value: patientData?.clinicName || batch?.clinic_name || batch?.clinicName },
    { label: 'Traitement', value: patientData?.treatmentType },
    { label: 'N° Lot', value: batchNumber },
    {
      label: "Nombre d'aligneurs",
      value: alignersTotal != null ? String(alignersTotal) : '—',
    },
    { label: 'Échéance', value: deadline },
    { label: 'Date fiche', value: patientData?.createdDate },
    { label: 'Référence', value: `PROD-${patientData?.caseNumber || '—'}` },
    ...extraFields,
  ];

  return (
    <div className="production-sheet-header border-b border-gray-200/80 p-4 md:p-5 print:p-2">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-4 print:gap-2 print:mb-2">
        <div className="min-w-0">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 leading-tight print:text-lg">
            Fiche de Production
          </h2>
          <p className="text-sm text-gray-600 mt-0.5 print:text-xs">
            Instructions atelier — Modèle, étiquettes et finition
          </p>
          <p className="text-xs text-gray-500 mt-0.5 print:hidden">ClearMotion Orthodontie</p>
        </div>

        <div className="flex shrink-0 flex-col items-center gap-1.5 self-center sm:self-start print:gap-0.5">
          <div className="rounded-lg border border-gray-200 bg-white p-2 print:p-1">
            {qrValue ? (
              <QRCodeSVG value={qrValue} size={112} level="M" includeMargin={false} className="print:hidden" />
            ) : null}
            {qrValue ? (
              <div className="hidden print:block">
                <QRCodeSVG value={qrValue} size={64} level="M" includeMargin={false} />
              </div>
            ) : (
              <div className="w-[112px] h-[112px] print:w-16 print:h-16 bg-gray-100 rounded flex items-center justify-center text-[10px] text-gray-400 text-center px-2">
                Lot indisponible
              </div>
            )}
          </div>
          <span className="text-[10px] font-medium text-gray-500 tracking-wide uppercase print:text-[8px]">
            Traçabilité
          </span>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white/90 overflow-hidden print:rounded-md">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 divide-x divide-y divide-gray-100 print:grid-cols-4">
          {fields.map((field) => (
            <div key={field.label} className="px-3 py-2.5 min-w-0 print:px-1.5 print:py-1">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-0.5 print:text-[7px] print:mb-0">
                {field.label}
              </div>
              <div className="text-sm font-semibold text-gray-900 break-all whitespace-normal leading-snug print:text-[10px]">
                {field.value || '—'}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProductionSheetHeader;
